<template>
  <div>
    <div class="left-right">
      <div class="button-left">
        <Icon @click.native="previous" name="left" />
      </div>
      <StepTitle v-if="selectedRoomData" :name="selectedRoomData.name" />
      <div class="button-right"><Icon @click.native="next" name="right" /></div>
    </div>
    <div class="flickity-div">
      <flickity
        :options="flickityOptions"
        ref="flickity"
        v-if="roomData && roomData.length > 0"
        v-on:init="initFlick"
      >
        <div
          v-for="room in roomData"
          class="carousel-cell custom-cell"
          :key="room.name"
        >
          <div class="carousel-cell-container" style="display: flex">
            <img :src="room.img" alt="" class="image" @load="loaded" />
            <div v-if="!room.isActive" class="overlay">
              <Icon name="locked" size="30%" />
            </div>
          </div>
        </div>
      </flickity>
      <div class="button-left">
        <Icon @click.native="previous" name="left" />
      </div>
      <div class="button-right">
        <Icon @click.native="next" name="right" />
      </div>
    </div>
    <SubTextContainer v-if="selectedRoomData && selectedRoomData.isActive">
      <SubTextTitle>{{ selectedRoomData.title }}</SubTextTitle>
      <span v-html="selectedRoomData.body" />
      <span class="soloButtonSpan">
        <Button @click.native="() => selectStep()">
          <span class="button-text-with-icon"
            >Code invullen <Icon name="cta" size="9"
          /></span>
        </Button>
      </span>
    </SubTextContainer>
    <SubTextContainer v-if="selectedRoomData && !selectedRoomData.isActive">
      <SubTextTitle
        >Deze kamer komt vrij op
        {{
          selectedRoomData.start
            .split('-')
            .reverse()
            .join('/')
        }}</SubTextTitle
      >
      <span
        ><br />Helaas, de code van deze kamer kan je pas later kraken.</span
      >
      <span class="soloButtonSpan">
        <Button @click.native="() => goToFirstFreeStep()">
          <span class="button-text-with-icon">
            Naar de wedstrijd <Icon name="cta" size="9" />
          </span>
        </Button>
      </span>
    </SubTextContainer>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import Flickity from 'vue-flickity';
import StepTitle from '@/components/StepTitle';
import Button from '@/components/Button';
import SubTextContainer from '@/components/SubTextContainer';
import SubTextTitle from '@/components/SubTextTitle';
import Icon from '@/components/Icon';

export default {
  name: 'CvcCarousel',
  data() {
    return {
      selectedIndex: 0,
      flickityOptions: {
        pageDots: false,
        prevNextButtons: false,
        // any options from Flickity can be used
      },
    };
  },
  computed: {
    ...mapState(['roomData', 'scrollToActive']),
    selectedRoomData() {
      return this.roomData ? this.roomData[this.selectedIndex] : null;
    },
  },
  components: {
    Icon,
    SubTextTitle,
    SubTextContainer,
    Button,
    StepTitle,
    Flickity,
  },
  methods: {
    ...mapMutations(['setStep', 'resetNavigateFlag']),
    initFlick() {
      // setting listeners
      this.$refs.flickity.on(
        'staticClick',
        (event, pointer, cellElement, cellIndex) => {
          if (this.roomData && this.roomData[cellIndex]) {
            this.$nextTick(() => {
              // for some reason, the click events propagate. In order to fix that, we timeout
              setTimeout(() => {
                this.selectStep(cellIndex);
              }, 1);
            });
          }
        },
      );
      this.$refs.flickity.on('change', index => {
        this.selectedIndex = index;
      });
    },
    selectStep(index = null) {
      if (index === this.selectedIndex || index === null) {
        this.setStep({ step: index ?? this.selectedIndex });
      } else {
        this.$refs.flickity.select(index);
      }
    },
    goToFirstFreeStep() {
      const askedStep = this.roomData.findIndex(
        room => room.isActive && !room.isFinished,
      );
      this.$refs.flickity.select(askedStep);
    },
    loaded() {
      // otherwise carousel has height 0
      if (this.$refs.flickity) {
        this.$refs.flickity.resize();
        this.$nextTick(() => {
          this.goToFirstFreeStep();
          this.resetNavigateFlag();
        });
      }
    },
    next() {
      this.$refs.flickity.next();
    },
    previous() {
      this.$refs.flickity.previous();
    },
  },
};
</script>

<style lang="scss">
.custom-cell {
  width: 100%;
  max-width: 680px;
  margin: 0 20px 0 0;
  padding: 0;

  .image {
    height: auto;
    width: 100%;
    max-width: 680px;
    min-height: 383px;
    max-height: 383px;
    border-radius: 10px;
    overflow: hidden;

    @media print, screen and (max-width: 710px) {
      min-height: 50vw;
      max-height: 50vw;
    }
  }

  .overlay {
    left: 0;
    top: 0;
    background-color: rgba(200, 200, 200, 0.7);
    position: absolute;
    height: 100%;
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    pointer-events: none;
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
  }
}
.flickity-viewport {
  overflow: visible;
}

.soloButtonSpan {
  padding: 26px 0 29px;
}

.flickity-div {
  position: relative;
  .button-right,
  .button-left {
    position: absolute;
    top: 50%;
    padding: 0 20px;

    > img {
      display: inline;

      @media print, screen and (max-width: 700px) {
        display: none;
      }
    }
  }

  .button-right {
    right: 0;
  }

  .button-left {
    left: 0;
  }
}

.left-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: space-between;
  margin-bottom: 30px;

  .button-left,
  .button-right {
    display: flex;
    flex: 0;
    width: 100%;
    text-align: right;
    position: relative;

    > img {
      display: none;

      @media print, screen and (max-width: 700px) {
        display: inline;
      }
    }
  }
}

.button-text-with-icon {
  display: flex;
  align-items: center;

  > img {
    margin-left: 8px;
  }
}
</style>
